/* Volume discount (ADR 0038) on the Payments page: the Payer's tier with the Next-tier hint
   in the header next to the wallet (press it for the full ladder), and the live line in the
   checkout bar showing the tier the cart lands on. */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VolumeDiscountSummary } from '../../api/client';
import { C } from '../ChatAssistant/compact/tokens';
import { font as f } from '../ChatAssistant/compact/style';
import { Icon } from '../ChatAssistant/compact/kit';
import { usdCents, usdShort } from './model';
import { Modal, ModalFoot, ModalHead, TextBtn } from './ui';
import { cartVolume, ladderRows, pct, volumeOffCents } from './volume';

export function VolumeBadge({ vd }: { vd: VolumeDiscountSummary }) {
  const { t } = useTranslation('paymentsCompact');
  const [open, setOpen] = useState(false);
  if (vd.source === 'off') return null;
  const special = vd.source === 'override';
  const next = special ? null : vd.next_tier;
  const hint = special ? null : next ? t('volume.badgeNext', { count: next.accounts_needed, pct: pct(next.bps) }) : t('volume.badgeTop');

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="pay-volume cpt-hover-border"
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px 8px 14px', background: C.card, border: `1px solid ${vd.bps > 0 ? 'var(--om-accent-deep)' : C.borderHi}`, borderRadius: 10, color: C.text, textAlign: 'left', cursor: 'pointer', boxSizing: 'border-box' }}
      >
        <span style={{ width: 34, height: 34, flex: 'none', borderRadius: 8, background: C.tint16, color: C.purpleLt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="percent" size={16} />
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
          <span style={{ font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.sub }}>{t('volume.title')}</span>
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap' }}>
            <span style={{ font: f(700, 20, 1.1), color: vd.bps > 0 ? C.purplePale : C.text, fontVariantNumeric: 'tabular-nums' }}>{pct(vd.bps)}</span>
            <span style={{ font: f(500, 11.5), color: C.sub }}>{special ? t('volume.badgeSpecial') : t('volume.paid', { count: vd.paid_count })}</span>
          </span>
          {hint && <span style={{ font: f(500, 11), color: next ? C.purpleLt : C.muted, whiteSpace: 'nowrap' }}>{hint}</span>}
        </span>
        <Icon name="chevron-right" size={14} color={C.muted} />
      </button>
      {open && <VolumeLadderDialog vd={vd} onClose={() => setOpen(false)} />}
    </>
  );
}

/** The full ladder: every step, the one the Payer is on, and how many more each one needs. */
function VolumeLadderDialog({ vd, onClose }: { vd: VolumeDiscountSummary; onClose: () => void }) {
  const { t } = useTranslation('paymentsCompact');
  const special = vd.source === 'override';
  const next = special ? null : vd.next_tier;
  const rows = ladderRows(vd);

  const label = special
    ? t('volume.special', { pct: pct(vd.bps) })
    : vd.bps > 0
      ? t('volume.on', { pct: pct(vd.bps), count: vd.paid_count })
      : t('volume.paid', { count: vd.paid_count });

  // Progress from the step the Payer is on towards the next one.
  const from = rows.reduce((m, r) => (r.bps != null && vd.paid_count >= r.min ? r.min : m), 0);
  const to = next ? vd.paid_count + next.accounts_needed : 0;
  const progress = next && to > from ? (vd.paid_count - from) / (to - from) : 1;

  const COLS = 'minmax(90px,1fr) 80px minmax(110px,1fr)';
  const cell = { display: 'grid', gridTemplateColumns: COLS, gap: 12, alignItems: 'center', padding: '0 12px' };

  return (
    <Modal width={460} onClose={onClose}>
      <ModalHead icon="percent" title={t('volume.title')} onClose={onClose} />
      <div style={{ padding: '6px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 12px', background: C.input, border: `1px solid ${C.border}`, borderRadius: 8 }}>
          <span style={{ font: f(600, 13), color: vd.bps > 0 ? C.purplePale : C.text }}>{label}</span>
          {next ? (
            <>
              <span style={{ font: f(400, 12.5, 1.45), color: C.sub }}>
                {t('volume.next', { count: next.accounts_needed, pct: pct(next.bps), amount: usdShort(vd.saving_cents / 100) })}
              </span>
              <span aria-hidden style={{ height: 4, borderRadius: 99, background: C.border, overflow: 'hidden' }}>
                <span style={{ display: 'block', height: '100%', width: Math.max(4, Math.min(100, progress * 100)) + '%', background: C.purple, borderRadius: 99 }} />
              </span>
            </>
          ) : !special ? (
            <span style={{ font: f(400, 12.5, 1.45), color: C.sub }}>{t('volume.top')}</span>
          ) : null}
        </div>

        <div role="table" style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
          <div role="row" style={{ ...cell, height: 32, background: C.input, font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>
            <span role="columnheader">{t('cond.col.accounts')}</span>
            <span role="columnheader" style={{ textAlign: 'right' }}>{t('cond.col.discount')}</span>
            <span role="columnheader" style={{ textAlign: 'right' }}>{t('volume.colProgress')}</span>
          </div>
          {rows.map((r) => (
            <div key={r.min} role="row" style={{ ...cell, height: 36, borderTop: `1px solid ${C.divider}`, background: r.current ? 'color-mix(in srgb, var(--om-accent) 10%, transparent)' : 'transparent', font: f(400, 12.5), fontVariantNumeric: 'tabular-nums', color: C.sub }}>
              <span role="cell" style={{ color: C.text, fontWeight: r.current ? 600 : 400 }}>{r.label}</span>
              {r.bps == null ? (
                // The negotiated 50+ has no % of its own: its note spans both right-hand columns.
                <span role="cell" style={{ gridColumn: '2 / span 2', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, textAlign: 'right' }}>
                  {t('cond.discNegotiated')}
                  {r.current && <Here />}
                </span>
              ) : (
                <>
                  <span role="cell" style={{ textAlign: 'right', fontWeight: 600, color: r.current ? C.purplePale : C.text }}>{pct(r.bps)}</span>
                  <span role="cell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    {r.current ? <Here /> : !special && r.needed > 0 ? t('volume.more', { count: r.needed }) : null}
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
        <span style={{ font: f(400, 12, 1.5), color: C.sub }}>{t('cond.discIntro')}</span>
      </div>
      <ModalFoot>
        <span style={{ flex: 1 }} />
        <TextBtn onClick={onClose}>{t('close')}</TextBtn>
      </ModalFoot>
    </Modal>
  );
}

function Here() {
  const { t } = useTranslation('paymentsCompact');
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: C.purpleLt, fontWeight: 600, whiteSpace: 'nowrap' }}>
      <Icon name="check" size={12} />
      {t('volume.here')}
    </span>
  );
}

export function CheckoutVolumeLine({ vd, cartCount, subtotalCents }: { vd: VolumeDiscountSummary; cartCount: number; subtotalCents: number }) {
  const { t } = useTranslation('paymentsCompact');
  if (vd.source === 'off') return null;
  const tier = cartVolume(vd, cartCount);
  const off = volumeOffCents(subtotalCents, tier.bps);

  let label: string;
  if (vd.source === 'override') label = t('volume.special', { pct: pct(tier.bps) });
  else if (tier.bps > 0) label = t('volume.cart', { added: cartCount, count: tier.count, pct: pct(tier.bps) });
  else if (tier.next) label = t('volume.unlock', { count: tier.next.accounts_needed, pct: pct(tier.next.bps) });
  else return null;

  return (
    <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, minWidth: 0, font: f(500, 12), color: tier.bps > 0 ? C.purplePale : C.sub }}>
      <Icon name="percent" size={12} />
      <span>{label}</span>
      {off > 0 && <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{'−' + usdCents(off)}</span>}
    </span>
  );
}
